import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  User, Mail, Phone, Briefcase, Globe, Edit3, Save, X, Upload, FileText,
  Calendar, CheckCircle2, Clock, AlertTriangle, Star, Shield, Trash2,
  ChevronDown, TrendingUp, Award, Eye
} from 'lucide-react';
import { formatAddress } from '@/lib/formatAddress';

const DOC_TYPES = [
  { key: 'kyc_aadhar', label: 'KYC - Aadhar Card' },
  { key: 'kyc_pan', label: 'KYC - PAN Card' },
  { key: 'kyc_passport', label: 'KYC - Passport' },
  { key: 'offer_letter', label: 'Offer Letter' },
  { key: 'appointment_letter', label: 'Appointment Letter' },
  { key: 'contract', label: 'Contract Agreement' },
  { key: 'nda', label: 'NDA (Non-Disclosure)' },
  { key: 'bank_details', label: 'Bank Details / Cancelled Cheque' },
  { key: 'resume', label: 'Resume / CV' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'other', label: 'Other Document' },
];

const LEAVE_TYPES = ['casual', 'sick', 'earned', 'maternity', 'paternity', 'unpaid', 'comp_off'];

export default function ProfilePage() {
  const params = useParams();
  const { api, user: currentUser } = useAuth();
  const viewUserId = params.userId;
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [tab, setTab] = useState('overview');
  const [docs, setDocs] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'casual', start_date: '', end_date: '', days: 1, reason: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('other');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  const userId = viewUserId || currentUser?.id;
  const isOwnProfile = userId === currentUser?.id;
  const isAdmin = ['master_admin', 'hr_manager', 'coo', 'studio_director'].includes(currentUser?.role);

  useEffect(() => {
    if (!userId) return;
    api.get(`/profile/${userId}`).then(r => {
      const data = r.data || {};
      // Coerce a structured address object → flat string so the dropdown +
      // typed-input both handle it without crashing. Keeps legacy users
      // (string address) working untouched.
      const safe = { ...data, address: formatAddress(data.address) };
      setProfile(safe);
      setEditForm(safe);
      setDocs(data.documents || []);
      setLeaves(data.leaves || []);
    }).catch(console.error);
    api.get(`/performance/${userId}`).then(r => setPerformance(r.data)).catch(() => {});
    api.get(`/work-orders?assignee_user_id=${userId}`).then(r => setWorkOrders(r.data || [])).catch(() => setWorkOrders([]));
  }, [api, userId]);

  const saveProfile = async () => {
    const saveData = { ...editForm };
    // Preserve avatar
    if (profile.avatar_url && !saveData.avatar_url) {
      saveData.avatar_url = profile.avatar_url;
    }
    // Convert skills/tools strings to arrays for storage
    if (typeof saveData.skills === 'string') {
      saveData.skills = saveData.skills.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (typeof saveData.tools === 'string') {
      saveData.tools = saveData.tools.split(',').map(s => s.trim()).filter(Boolean);
    }
    const res = await api.put('/profile/update', saveData);
    setProfile(prev => ({ ...prev, ...res.data, avatar_url: prev.avatar_url || res.data.avatar_url }));
    setEditing(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/profile/${userId}/upload-photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    if (photoRef.current) photoRef.current.value = '';
  };

  const viewDocument = async (docId) => {
    setLoadingDoc(true);
    try {
      const res = await api.get(`/profile/${userId}/docs/${docId}/view`);
      setViewingDoc(res.data);
    } catch (err) { alert('Cannot view document'); }
    setLoadingDoc(false);
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', uploadDocType);
    try {
      await api.post(`/profile/${userId}/upload-doc`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = await api.get(`/profile/${userId}/docs`);
      setDocs(d.data);
    } catch (err) { console.error(err); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteDoc = async (docId) => {
    await api.delete(`/profile/${userId}/docs/${docId}`);
    setDocs(prev => prev.filter(d => d.id !== docId));
  };

  const applyLeave = async () => {
    await api.post('/leaves/apply', leaveForm);
    setShowLeaveForm(false);
    const l = await api.get(`/leaves?user_id=${userId}`);
    setLeaves(l.data);
  };

  const handleLeaveAction = async (leaveId, action) => {
    await api.put(`/leaves/${leaveId}/${action}`);
    const l = await api.get(`/leaves?user_id=${userId}`);
    setLeaves(l.data);
  };

  if (!profile) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading profile...</div></div>;

  const approvedLeaves = leaves.filter(l => l.status === 'approved');
  const totalLeaveDays = approvedLeaves.reduce((s, l) => s + (l.days || 1), 0);

  return (
    <div className="space-y-4" data-testid="profile-page">
      {/* Profile Header — Employee Card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500" />

        <div className="p-5">
          <div className="flex gap-5">
            {/* Photo */}
            <div className="flex-shrink-0">
              <div className="relative group">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="w-24 h-24 rounded-sm object-cover bg-zinc-800" />
                ) : (
                  <div className="w-24 h-24 bg-zinc-800 rounded-sm flex items-center justify-center text-3xl font-heading font-bold text-zinc-500">
                    {profile.full_name?.charAt(0) || '?'}
                  </div>
                )}
                <input ref={photoRef} type="file" accept=".png,.jpg,.jpeg" onChange={uploadPhoto} className="hidden" />
                <button onClick={() => photoRef.current?.click()}
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-sm flex items-center justify-center">
                  <Upload className="w-5 h-5 text-white" />
                </button>
              </div>
              <button onClick={() => photoRef.current?.click()} className="mt-1.5 text-[9px] text-zinc-600 hover:text-blue-400 w-24 text-center block">
                {profile.avatar_url ? 'Change Photo' : 'Upload Photo'}
              </button>
            </div>

            {/* Main Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  {editing ? (
                    <input value={editForm.full_name || ''} onChange={e => setEditForm({...editForm, full_name: e.target.value})}
                      className="text-xl font-heading font-bold text-white bg-zinc-950 border border-zinc-700 rounded-sm px-2 h-9 w-64 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  ) : (
                    <h1 className="text-xl font-heading font-bold text-white">{profile.full_name}</h1>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-blue-500/15 text-blue-400 border border-blue-500/20 font-medium">
                      {profile.role?.replace(/_/g, ' ')}
                    </span>
                    {profile.department && <span className="text-[10px] px-2 py-0.5 rounded-sm bg-zinc-800 text-zinc-400">{profile.department}</span>}
                    <span className={`status-${profile.is_active ? 'completed' : 'blocked'} px-1.5 py-0.5 rounded-full text-[9px]`}>
                      {profile.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {profile.nda_status === 'signed' && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-sm border border-emerald-500/20">NDA Signed</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {editing ? (
                    <>
                      <button onClick={saveProfile} className="h-8 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-sm flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save Changes</button>
                      <button onClick={() => { setEditing(false); setEditForm(profile); }} className="h-8 px-3 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
                    </>
                  ) : (
                    <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="h-8 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm flex items-center gap-1.5"><Edit3 className="w-3.5 h-3.5" /> Edit Profile</button>
                  )}
                </div>
              </div>

              {/* Detail Grid */}
              {!editing ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mt-4 pt-3 border-t border-zinc-800/50">
                  <div><p className="text-[9px] text-zinc-600 uppercase">Email</p><p className="text-xs text-zinc-300 font-mono">{profile.email}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Phone</p><p className="text-xs text-zinc-300">{profile.phone || '-'}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Date of Birth</p><p className="text-xs text-zinc-300">{profile.dob || '-'}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">City</p><p className="text-xs text-zinc-300">{formatAddress(profile.address) || '-'}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Skills</p><p className="text-xs text-zinc-300">{Array.isArray(profile.skills) ? profile.skills.join(', ') : profile.skills || '-'}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Tools</p><p className="text-xs text-zinc-300">{Array.isArray(profile.tools) ? profile.tools.join(', ') : profile.tools || '-'}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Portfolio</p>{profile.portfolio_url ? <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline truncate block">View Portfolio</a> : <p className="text-xs text-zinc-600">-</p>}</div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Employee ID</p><p className="text-[10px] text-zinc-500 font-mono">{profile.id?.slice(0, 8)}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Aadhar Number</p><p className="text-xs text-zinc-300 font-mono">{profile.aadhar_number || (profile.aadhar_last4 ? `XXXX-XXXX-${profile.aadhar_last4}` : '-')}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Bank Account</p><p className="text-xs text-zinc-300 font-mono">{profile.bank_account_number || (profile.bank_account_last4 ? `XXXXXXXX${profile.bank_account_last4}` : '-')}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">Joined</p><p className="text-xs text-zinc-300">{profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}</p></div>
                  <div><p className="text-[9px] text-zinc-600 uppercase">NDA</p><p className={`text-xs ${profile.nda_status === 'signed' ? 'text-emerald-400' : 'text-amber-400'}`}>{profile.nda_status || 'pending'}</p></div>
                  {profile.bio && <div className="col-span-2 md:col-span-4"><p className="text-[9px] text-zinc-600 uppercase">Bio</p><p className="text-xs text-zinc-400 italic">{profile.bio}</p></div>}
                  {profile.emergency_contact && <div className="col-span-2"><p className="text-[9px] text-zinc-600 uppercase">Emergency Contact</p><p className="text-xs text-zinc-300">{profile.emergency_contact}</p></div>}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 pt-3 border-t border-zinc-800/50">
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Email</label>
                    <input value={editForm.email || ''} onChange={e => setEditForm({...editForm, email: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="your@email.com" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Phone</label>
                    <input value={editForm.phone || ''} onChange={e => setEditForm({...editForm, phone: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="+91-9876543210" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Date of Birth</label>
                    <input value={editForm.dob || ''} onChange={e => setEditForm({...editForm, dob: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="DD/MM/YYYY" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Skills</label>
                    <input value={Array.isArray(editForm.skills) ? editForm.skills.join(', ') : (editForm.skills || '')}
                      onChange={e => setEditForm({...editForm, skills: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="Business Development, 3D Modeling" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Tools</label>
                    <input value={Array.isArray(editForm.tools) ? editForm.tools.join(', ') : (editForm.tools || '')}
                      onChange={e => setEditForm({...editForm, tools: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="Blender, Maya, ZBrush" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Portfolio URL</label>
                    <input value={editForm.portfolio_url || ''} onChange={e => setEditForm({...editForm, portfolio_url: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="https://artstation.com/you" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Bio / About</label>
                    <input value={editForm.bio || ''} onChange={e => setEditForm({...editForm, bio: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="Founder & CEO, VawWorldMedia" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Emergency Contact</label>
                    <input value={editForm.emergency_contact || ''} onChange={e => setEditForm({...editForm, emergency_contact: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="Name - Phone number" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Aadhar Number (from doc)</label>
                    <input value={editForm.aadhar_number || ''} onChange={e => setEditForm({...editForm, aadhar_number: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none font-mono" placeholder="1234-5678-9012" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Bank Account Number (from doc)</label>
                    <input value={editForm.bank_account_number || ''} onChange={e => setEditForm({...editForm, bank_account_number: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none font-mono" placeholder="Full account number" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">Bank Name & IFSC</label>
                    <input value={editForm.bank_ifsc || ''} onChange={e => setEditForm({...editForm, bank_ifsc: e.target.value})}
                      className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="SBI - SBIN0001234" />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-600 uppercase">City</label>
                    <div className="flex gap-1">
                      <select value={editForm.address && !['Mumbai, Maharashtra','Delhi NCR','Bengaluru, Karnataka','Hyderabad, Telangana','Chennai, Tamil Nadu','Kolkata, West Bengal','Pune, Maharashtra','Ahmedabad, Gujarat','Jaipur, Rajasthan','Lucknow, UP','Chandigarh, Punjab','Bhopal, MP','Indore, MP','Thiruvananthapuram, Kerala','Kochi, Kerala','Goa','Dehradun, Uttarakhand','Noida, UP','Gurugram, Haryana','Coimbatore, Tamil Nadu','Vadodara, Gujarat','Surat, Gujarat','Nagpur, Maharashtra','Nashik, Maharashtra','Visakhapatnam, AP','Bhubaneswar, Odisha','Patna, Bihar','Ranchi, Jharkhand','Los Angeles, USA','New York, USA','London, UK','Toronto, Canada','Dubai, UAE','Singapore','Tokyo, Japan','Seoul, South Korea','Sydney, Australia','Berlin, Germany','Paris, France','Vancouver, Canada','Manila, Philippines','Bangkok, Thailand','Remote / Work from Home'].includes(editForm.address) ? 'Other' : (editForm.address || '')}
                        onChange={e => { if (e.target.value !== 'Other') setEditForm({...editForm, address: e.target.value}); else setEditForm({...editForm, address: ''}); }}
                        className="flex-1 h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-1 text-[10px] text-zinc-300 focus:outline-none">
                        <option value="">Select City</option>
                        {['Mumbai, Maharashtra','Delhi NCR','Bengaluru, Karnataka','Hyderabad, Telangana','Chennai, Tamil Nadu','Kolkata, West Bengal','Pune, Maharashtra','Ahmedabad, Gujarat','Jaipur, Rajasthan','Lucknow, UP','Chandigarh, Punjab','Bhopal, MP','Indore, MP','Goa','Noida, UP','Gurugram, Haryana','Los Angeles, USA','London, UK','Dubai, UAE','Singapore','Remote / Work from Home','Other'].map(c =>
                          <option key={c} value={c}>{c}</option>
                        )}
                      </select>
                      {(editForm.address === '' || (editForm.address && !['Mumbai, Maharashtra','Delhi NCR','Bengaluru, Karnataka','Hyderabad, Telangana','Chennai, Tamil Nadu','Kolkata, West Bengal','Pune, Maharashtra','Ahmedabad, Gujarat','Jaipur, Rajasthan','Lucknow, UP','Chandigarh, Punjab','Bhopal, MP','Indore, MP','Goa','Noida, UP','Gurugram, Haryana','Los Angeles, USA','London, UK','Dubai, UAE','Singapore','Remote / Work from Home'].includes(editForm.address))) && (
                        <input value={editForm.address || ''} onChange={e => setEditForm({...editForm, address: e.target.value})}
                          className="flex-1 h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none" placeholder="Type city name" />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {[
          { k: 'overview', l: 'Overview' },
          { k: 'documents', l: `Documents (${docs.length})` },
          { k: 'leave', l: `Leave (${totalLeaveDays} days)` },
          { k: 'performance', l: 'Performance' },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? 'border-blue-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mb-2" />
              <p className="text-xl font-heading font-bold text-emerald-400">{profile.task_stats?.completed || 0}</p>
              <p className="text-[10px] text-zinc-500">Tasks Completed</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <Clock className="w-4 h-4 text-blue-400 mb-2" />
              <p className="text-xl font-heading font-bold text-blue-400">{profile.task_stats?.in_progress || 0}</p>
              <p className="text-[10px] text-zinc-500">In Progress</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <Calendar className="w-4 h-4 text-amber-400 mb-2" />
              <p className="text-xl font-heading font-bold text-amber-400">{totalLeaveDays}</p>
              <p className="text-[10px] text-zinc-500">Leave Days Used</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <Award className="w-4 h-4 text-violet-400 mb-2" />
              <p className="text-xl font-heading font-bold text-violet-400">{performance?.summary?.kudos_days || 0}</p>
              <p className="text-[10px] text-zinc-500">Kudos Days</p>
            </div>
          </div>

          {/* Password Management — Admin Only */}
          {isAdmin && !isOwnProfile && (
            <div className="bg-zinc-900 border border-amber-500/20 rounded-sm p-4">
              <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Admin: Password Management
              </h4>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-[9px] text-zinc-500 uppercase mb-1 block">Set New Password</label>
                  <input id="admin-new-pass" type="text" defaultValue="" placeholder="Enter new password (min 6 chars)"
                    className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white font-mono focus:ring-1 focus:ring-amber-500 focus:outline-none" />
                </div>
                <button onClick={async () => {
                  const pw = document.getElementById('admin-new-pass').value;
                  if (!pw || pw.length < 6) { alert('Password must be at least 6 characters'); return; }
                  try {
                    await api.post(`/users/${userId}/reset-password`, { password: pw });
                    alert(`Password changed successfully!\n\nNew password: ${pw}\n\nShare this with the user securely.`);
                    document.getElementById('admin-new-pass').value = '';
                  } catch (e) { alert('Failed to reset password'); }
                }} className="h-9 px-5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded-sm whitespace-nowrap">
                  Reset Password
                </button>
              </div>
              <p className="text-[9px] text-zinc-600 mt-2">This will immediately change the user's password. Share the new password with them securely.</p>
            </div>
          )}

          {(profile.workforce_type === 'freelancer' || profile.workforce_type === 'vendor' || profile.role === 'freelancer' || profile.role?.startsWith('vendor_')) && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="profile-work-orders-panel">
              <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-400" /> Work Orders & Commercial Scope
              </h4>
              <div className="divide-y divide-zinc-800/50">
                {workOrders.map(wo => (
                  <div key={wo.id} className="py-2 flex items-center justify-between gap-3" data-testid={`profile-work-order-${wo.id}`}>
                    <div>
                      <p className="text-xs text-zinc-200">{wo.work_order_no} · {wo.project_name}</p>
                      <p className="text-[10px] text-zinc-600">{wo.lines?.map(l => `${l.label || l.stage}: ${l.quantity} ${l.billing_unit} × ₹${l.rate}`).join(' · ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-mono text-emerald-400">₹{(wo.total_amount || 0).toLocaleString('en-IN')}</p>
                      <span className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded-sm">{wo.status}</span>
                    </div>
                  </div>
                ))}
                {workOrders.length === 0 && <p className="text-xs text-zinc-600 text-center py-4">No work orders generated yet.</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {tab === 'documents' && (
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Upload required documents. Accepts PNG, JPG, PDF files.</p>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="divide-y divide-zinc-800/30">
              {DOC_TYPES.map(dtype => {
                const typeDocs = docs.filter(d => d.doc_type === dtype.key);
                const hasDoc = typeDocs.length > 0;
                return (
                  <div key={dtype.key} className="px-4 py-3 flex items-center justify-between group" data-testid={`doc-row-${dtype.key}`}>
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${hasDoc ? 'bg-emerald-500/15' : 'bg-zinc-800'}`}>
                        {hasDoc ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <FileText className="w-4 h-4 text-zinc-600" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-xs ${hasDoc ? 'text-zinc-200' : 'text-zinc-400'}`}>{dtype.label}</p>
                        {hasDoc ? (
                          <div className="flex flex-wrap gap-2 mt-0.5">
                            {typeDocs.map(d => (
                              <span key={d.id} className="inline-flex items-center gap-1.5 text-[9px] font-mono px-2 py-0.5 bg-zinc-800 rounded text-emerald-400">
                                <button onClick={() => viewDocument(d.id)} className="hover:text-blue-400 underline">{d.filename}</button>
                                <button onClick={() => viewDocument(d.id)} className="text-blue-400 hover:text-blue-300" title="View document"><Eye className="w-2.5 h-2.5" /></button>
                                <button onClick={() => deleteDoc(d.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[9px] text-zinc-700">Not uploaded — click Upload to add</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {hasDoc && (
                        <button onClick={() => viewDocument(typeDocs[0].id)}
                          className="h-7 px-2.5 bg-violet-500/20 text-violet-400 text-[10px] rounded-sm hover:bg-violet-500/30 flex items-center gap-1">
                          <Eye className="w-3 h-3" /> View
                        </button>
                      )}
                      <input type="file" id={`file-${dtype.key}`} className="hidden" accept=".png,.jpg,.jpeg,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploading(true);
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('doc_type', dtype.key);
                          try {
                            await api.post(`/profile/${userId}/upload-doc`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                            const d = await api.get(`/profile/${userId}/docs`);
                            setDocs(d.data);
                          } catch (err) { console.error(err); }
                          setUploading(false);
                          e.target.value = '';
                        }} />
                      <button onClick={() => document.getElementById(`file-${dtype.key}`).click()}
                        className={`h-7 px-3 text-[10px] rounded-sm flex items-center gap-1.5 transition-all ${
                          hasDoc ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700' : 'bg-blue-600 hover:bg-blue-500 text-white'
                        }`}>
                        <Upload className="w-3 h-3" /> {hasDoc ? 'Replace' : 'Upload'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-4 text-[9px] text-zinc-600 px-1">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Uploaded</span>
            <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-zinc-700" /> Missing</span>
            <span>Accepted: PNG, JPG, PDF</span>
          </div>
        </div>
      )}

      {/* Leave Tab */}
      {tab === 'leave' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex gap-4">
              <span className="text-xs text-zinc-500">Total: <span className="text-white font-mono">{totalLeaveDays}</span> days</span>
              <span className="text-xs text-zinc-500">Pending: <span className="text-amber-400 font-mono">{leaves.filter(l => l.status === 'pending').length}</span></span>
            </div>
            {isOwnProfile && (
              <button onClick={() => setShowLeaveForm(true)} className="h-7 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[10px] rounded-sm flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Apply Leave
              </button>
            )}
          </div>

          {showLeaveForm && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <h4 className="text-xs font-semibold text-zinc-300 mb-3">Apply for Leave</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><label className="text-[9px] text-zinc-600 uppercase">Type</label>
                  <select value={leaveForm.leave_type} onChange={e => setLeaveForm({...leaveForm, leave_type: e.target.value})} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-300">
                    {LEAVE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select></div>
                <div><label className="text-[9px] text-zinc-600 uppercase">From</label>
                  <input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-300" /></div>
                <div><label className="text-[9px] text-zinc-600 uppercase">To</label>
                  <input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-300" /></div>
                <div><label className="text-[9px] text-zinc-600 uppercase">Days</label>
                  <input type="number" min="0.5" step="0.5" value={leaveForm.days} onChange={e => setLeaveForm({...leaveForm, days: parseFloat(e.target.value)})} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-300" /></div>
              </div>
              <div className="mt-2"><label className="text-[9px] text-zinc-600 uppercase">Reason</label>
                <input value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white" placeholder="Leave reason" /></div>
              <div className="flex gap-2 mt-3">
                <button onClick={applyLeave} className="h-7 px-4 bg-blue-600 text-white text-[10px] rounded-sm">Submit</button>
                <button onClick={() => setShowLeaveForm(false)} className="h-7 px-4 bg-zinc-800 text-zinc-400 text-[10px] rounded-sm">Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="divide-y divide-zinc-800/30">
              {leaves.map(l => (
                <div key={l.id} className="px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-200 capitalize">{l.leave_type?.replace('_', ' ')}</span>
                      <span className={`status-${l.status === 'approved' ? 'completed' : l.status === 'rejected' ? 'blocked' : 'pending'} px-1.5 py-0.5 rounded-full text-[9px]`}>{l.status}</span>
                      <span className="text-[10px] font-mono text-zinc-600">{l.days} day{l.days > 1 ? 's' : ''}</span>
                    </div>
                    <p className="text-[10px] text-zinc-600">{l.start_date} to {l.end_date} | {l.reason}</p>
                  </div>
                  {l.status === 'pending' && isAdmin && (
                    <div className="flex gap-1">
                      <button onClick={() => handleLeaveAction(l.id, 'approve')} className="h-6 px-2 bg-emerald-500/20 text-emerald-400 text-[9px] rounded-sm">Approve</button>
                      <button onClick={() => handleLeaveAction(l.id, 'reject')} className="h-6 px-2 bg-red-500/20 text-red-400 text-[9px] rounded-sm">Reject</button>
                    </div>
                  )}
                </div>
              ))}
              {leaves.length === 0 && <p className="text-xs text-zinc-600 text-center py-6">No leave records</p>}
            </div>
          </div>
        </div>
      )}

      {/* Performance Tab */}
      {tab === 'performance' && performance && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
              <p className="text-lg font-heading font-bold text-white">{performance.summary.completed}</p>
              <p className="text-[9px] text-zinc-500">Completed</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
              <p className="text-lg font-heading font-bold text-blue-400">{performance.summary.completion_rate}%</p>
              <p className="text-[9px] text-zinc-500">Rate</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
              <p className="text-lg font-heading font-bold text-emerald-400">{performance.summary.kudos_days}</p>
              <p className="text-[9px] text-zinc-500">Kudos Days</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
              <p className="text-lg font-heading font-bold text-green-400">{performance.summary.on_target_days}</p>
              <p className="text-[9px] text-zinc-500">On Target</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
              <p className="text-lg font-heading font-bold text-red-400">{performance.summary.below_days}</p>
              <p className="text-[9px] text-zinc-500">Below Target</p>
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Daily Output (Last 30 Days)</h4>
            <div className="flex flex-wrap gap-1">
              {performance.daily.map(d => (
                <div key={d.date} title={`${d.date}: ${d.delivered}/${d.target} tasks`}
                  className="w-7 h-7 rounded-sm flex items-center justify-center text-[8px] font-mono text-white"
                  style={{ backgroundColor: d.color }}>
                  {d.delivered}
                  {d.kudos && <Star className="w-2 h-2 text-amber-300 absolute" />}
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-[9px] text-zinc-600">
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-[#166534]" /> Over delivery (Kudos)</span>
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-[#10b981]" /> On target</span>
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-[#ef4444]" /> Below target</span>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="doc-viewer-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm w-[50rem] max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-heading font-bold text-white">{viewingDoc.filename}</h3>
                <p className="text-[10px] text-zinc-500">{viewingDoc.doc_type?.replace(/_/g, ' ')} | {viewingDoc.content_type}</p>
              </div>
              <div className="flex gap-2">
                {viewingDoc.content_type?.startsWith('image') && (
                  <span className="text-[9px] px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-sm">Verified: Visually check below</span>
                )}
                <button onClick={() => setViewingDoc(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-zinc-950">
              {viewingDoc.content_type?.startsWith('image') ? (
                <img src={viewingDoc.data_url} alt={viewingDoc.filename} className="max-w-full max-h-[65vh] object-contain rounded-sm" />
              ) : viewingDoc.content_type === 'application/pdf' ? (
                <iframe src={viewingDoc.data_url} title={viewingDoc.filename} className="w-full h-[65vh] rounded-sm" />
              ) : (
                <p className="text-xs text-zinc-500">Preview not available for this file type. Download to view.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {loadingDoc && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="text-xs text-zinc-400 animate-pulse-slow">Loading document...</div>
        </div>
      )}
    </div>
  );
}
